import { COLOR } from "../tokens"
import { formatVnd } from "../format/money"

// `remaining` can be negative now (2026-09 — budgets are no longer hard-gated
// at 0, an over-committed/over-paid line just goes negative). A negative
// remaining used to render as a 0-width segment via `Math.max(0, remaining)`,
// silently hiding the overage — now it renders as its own red "Over budget"
// segment instead, and the bar's total widens to fit it (never shrinks below
// `total` for the normal, non-overrun case).
export const UtilizationBar = ({ committed = 0, unpaid = 0, paid = 0, remaining = 0, total }) => {
  const consumed = committed + unpaid + paid
  const over = Math.max(0, -remaining)
  const t = Math.max(total || consumed + Math.max(0, remaining), consumed)
  const segs = [
    { key: "committed", n: committed, color: COLOR.warning600, label: "Committed" },
    { key: "unpaid", n: unpaid, color: COLOR.info600, label: "Approved – unpaid" },
    { key: "paid", n: paid, color: COLOR.success600, label: "Approved – paid" },
    { key: "remaining", n: Math.max(0, remaining), color: COLOR.border, label: "Remaining" },
    { key: "over", n: over, color: COLOR.danger600, label: "Over budget" },
  ]
  const title = segs.map((s) => `${s.label}: ${formatVnd(s.n)}`).join(" · ")
  return (
    <div className="ds-util" title={title} role="img" aria-label={title}>
      {segs.map((s) => {
        const pct = t > 0 ? (s.n / t) * 100 : 0
        if (pct <= 0) return null
        return <span key={s.key} style={{ width: `${pct}%`, background: s.color }} />
      })}
    </div>
  )
}

export const WaterfallBar = ({ committed = 0, unpaid = 0, paid = 0, remaining = 0, total = 0 }) => {
  const consumed = committed + unpaid + paid
  const over = Math.max(0, -remaining)
  const t = Math.max(total || consumed + Math.max(0, remaining), consumed)
  const segs = [
    { key: "committed", n: committed, color: COLOR.warning50, fg: COLOR.warning600, label: "Committed" },
    { key: "unpaid", n: unpaid, color: COLOR.info50, fg: COLOR.info600, label: "Unpaid" },
    { key: "paid", n: paid, color: COLOR.success50, fg: COLOR.success600, label: "Paid" },
    { key: "remaining", n: Math.max(0, remaining), color: COLOR.neutral50, fg: COLOR.neutral600, label: "Remaining" },
    { key: "over", n: over, color: COLOR.danger50, fg: COLOR.danger600, label: "Over budget" },
  ]
  return (
    <div>
      <div className="ds-waterfall">
        {segs.map((s) => {
          const pct = t > 0 ? (s.n / t) * 100 : 0
          if (pct <= 0) return null
          return (
            <span
              key={s.key}
              style={{ width: `${pct}%`, background: s.color, color: s.fg }}
              title={`${s.label}: ${formatVnd(s.n)}`}
            >
              {pct >= 12 ? formatVnd(s.n) : ""}
            </span>
          )
        })}
      </div>
      <div className="ds-waterfall-legend">
        {segs.map((s) => (
          <span key={s.key}>
            <i className="ds-legend-swatch" style={{ background: s.fg }} />
            {s.label}
          </span>
        ))}
      </div>
    </div>
  )
}

export const ProgressDots = ({ steps = [], current }) => {
  const idx = steps.findIndex((s) => s.key === current)
  const active = idx < 0 ? 0 : idx
  return (
    <div className="ds-dots" title={steps.map((s) => s.label).join(" → ")}>
      {steps.map((s, i) => (
        <span key={s.key} style={{ display: "inline-flex", alignItems: "center" }}>
          {i > 0 && <i />}
          <span
            className={i < active ? "is-done" : i === active ? "is-current" : ""}
            title={s.label}
          />
        </span>
      ))}
    </div>
  )
}

export default UtilizationBar
